import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import { Trash, FloppyDisk } from "@phosphor-icons/react";

export default function IdleTrackerConfigTab() {
  const { confirm } = useDialog();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [purging, setPurging] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/idle-tracker/config");
      setCfg(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.put("/idle-tracker/config", cfg);
      setCfg(r.data);
      setMsg("Saved.");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const purge = async () => {
    const ok = await confirm({
      title: "Purge old screenshots?",
      description: `All screenshots older than ${cfg?.screenshot_retention_days} days will be permanently deleted. This cannot be undone.`,
      confirmText: "Purge screenshots",
      variant: "destructive",
    });
    if (!ok) return;
    setPurging(true); setErr(""); setMsg("");
    try {
      const r = await api.post("/idle-tracker/purge");
      setMsg(`Purged: ${r.data.screenshots_deleted} screenshots, ${r.data.events_deleted} events (older than ${r.data.cutoff_date}).`);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPurging(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const num = (k, min, max, label, unit) => (
    <div>
      <label className="eyebrow block mb-2">{label} <span className="text-muted-foreground normal-case tracking-normal">({unit})</span></label>
      <input
        type="number" min={min} max={max}
        value={cfg[k]}
        onChange={e => setCfg({...cfg, [k]: Number(e.target.value)})}
        className="w-full border border-border px-3 py-2 text-sm"
        data-testid={`idle-cfg-${k}`}
      />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="eyebrow mb-1">Idle screen time tracking</div>
        <h2 className="font-display text-2xl font-bold">Configure activity monitoring</h2>
        <p className="text-sm text-muted-foreground mt-2">
          When enabled, users with the <b>use_idle_tracker</b> capability will have their idle time and (optionally) periodic screenshots recorded while they are checked-in on Attendance. Users are shown a persistent banner and prompted for consent before screen capture begins.
        </p>
      </div>

      <div className="border border-border p-6 space-y-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={e => setCfg({...cfg, enabled: e.target.checked})}
            className="mt-1"
            data-testid="idle-cfg-enabled"
          />
          <div>
            <div className="font-semibold">Enable idle tracker</div>
            <div className="text-xs text-muted-foreground">Master switch. Turning this off disables all client-side capture.</div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg.only_when_checked_in}
            onChange={e => setCfg({...cfg, only_when_checked_in: e.target.checked})}
            className="mt-1"
            data-testid="idle-cfg-only-checked-in"
          />
          <div>
            <div className="font-semibold">Only track while checked-in</div>
            <div className="text-xs text-muted-foreground">Recommended. Recording stops the moment the user checks out.</div>
          </div>
        </label>

        <div className="grid grid-cols-2 gap-4">
          {num("idle_threshold_seconds", 30, 7200, "Idle threshold", "seconds without activity")}
          {num("heartbeat_interval_seconds", 15, 600, "Heartbeat interval", "seconds")}
        </div>
      </div>

      <div className="border border-border p-6 space-y-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg.screenshot_enabled}
            onChange={e => setCfg({...cfg, screenshot_enabled: e.target.checked})}
            className="mt-1"
            data-testid="idle-cfg-screenshot-enabled"
          />
          <div>
            <div className="font-semibold">Enable screenshot capture</div>
            <div className="text-xs text-muted-foreground">
              Users must approve screen sharing on check-in. If they decline, they cannot check in.
              Screenshots are captured periodically and stored in your database.
            </div>
          </div>
        </label>

        <div className="grid grid-cols-2 gap-4">
          {num("screenshot_interval_minutes", 1, 120, "Screenshot interval", "minutes")}
          {num("screenshot_retention_days", 1, 365, "Retention", "days before auto-purge")}
        </div>

        <div className="pt-3 border-t border-border flex items-center gap-3 flex-wrap">
          <button
            onClick={purge}
            disabled={purging}
            className="border border-red-400 text-red-700 px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
            data-testid="idle-cfg-purge"
          >
            <Trash size={14} weight="bold" /> {purging ? "Purging…" : "Purge old data now"}
          </button>
          <div className="text-xs text-muted-foreground">
            Manually delete screenshots older than the retention window.
          </div>
        </div>
      </div>

      {msg && <div className="border border-[#22C55E] bg-[#F0FDF4] text-[#166534] px-4 py-2 text-sm">{msg}</div>}
      {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{err}</div>}

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="idle-cfg-save">
          <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
